import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import * as XLSX from 'xlsx';
import { parseZymplerFile, validateZymplerData } from '@/lib/zymplerParser';
import { ZymplerImportData, BusinesscaseRevenue } from '@/types/businesscase';
import { supabase } from '@/integrations/supabase/client';

export function useZymplerImport(businesscaseId: string, existingRevenues: BusinesscaseRevenue[]) {
  const { toast } = useToast();
  const [importData, setImportData] = useState<ZymplerImportData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  // Step 1: Parse file
  const handleFileUpload = async (file: File) => {
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: '❌ Bestand te groot',
        description: 'Maximum bestandsgrootte is 10MB',
        variant: 'destructive',
      });
      return;
    }

    // Validate file type
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(extension)) {
      toast({
        title: '❌ Ongeldig bestandstype',
        description: 'Alleen .xlsx, .xls en .csv bestanden zijn toegestaan',
        variant: 'destructive',
      });
      return;
    }

    // Check for duplicate upload
    if (uploadedFileName === file.name) {
      const confirmed = window.confirm(
        `Bestand '${file.name}' is al geüpload. Wilt u het opnieuw importeren?`
      );
      if (!confirmed) return;
    }

    setIsProcessing(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const parsed = await parseZymplerFile(jsonData, existingRevenues);
      parsed.fileName = file.name;
      
      setImportData(parsed);
      setUploadedFileName(file.name);
      
      toast({
        title: '📄 Bestand verwerkt',
        description: `${parsed.totalRows} rijen gevonden, ${parsed.mappedColumns.length} kolommen gemapped`,
        duration: 5000,
      });
    } catch (error: any) {
      console.error('Error parsing file:', error);
      toast({
        title: '❌ Fout bij verwerken',
        description: error.message || 'Kon bestand niet verwerken',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Step 2: Update preview data
  const updatePreviewRow = (yearIndex: number, field: string, value: number) => {
    if (!importData) return;
    
    const updated = { ...importData };
    const updatedRows = [...updated.parsedData];
    updatedRows[yearIndex] = {
      ...updatedRows[yearIndex],
      mapped: {
        ...updatedRows[yearIndex].mapped,
        [field]: value,
      },
    };
    updated.parsedData = updatedRows;
    setImportData(updated);
  };

  // Step 3: Import to database
  const executeImport = async () => {
    if (!importData) return false;

    setIsProcessing(true);
    try {
      const validation = validateZymplerData(importData.parsedData);
      if (!validation.isValid) {
        throw new Error(`Validatie gefaald: ${validation.errors.join(', ')}`);
      }

      // Batch processing for better performance
      const BATCH_SIZE = 100;
      let successCount = 0;

      for (let i = 0; i < importData.parsedData.length; i += BATCH_SIZE) {
        const batch = importData.parsedData.slice(i, i + BATCH_SIZE);
        
        for (const row of batch) {
          const existing = existingRevenues.find(r => r.year === row.year);
          
          if (existing) {
            // Update existing
            const { error } = await supabase
              .from('businesscase_revenues')
              .update({
                ...row.mapped,
                updated_at: new Date().toISOString(),
              })
              .eq('id', existing.id);
            
            if (error) throw error;
          } else {
            // Insert new
            const { error } = await supabase
              .from('businesscase_revenues')
              .insert({
                businesscase_id: businesscaseId,
                year: row.year,
                ...row.mapped,
              });
            
            if (error) throw error;
          }
          
          successCount++;
        }
      }

      toast({
        title: '✅ Zympler-export succesvol geïmporteerd in Greenvision CRM',
        description: `${successCount} jaren geïmporteerd, ${importData.mappedColumns.length} kolommen gemapped`,
        duration: 7000,
      });

      // Reset state
      setImportData(null);
      setUploadedFileName(null);
      
      return true;
    } catch (error: any) {
      console.error('Import error:', error);
      toast({
        title: '❌ Import gefaald',
        description: error.message || 'Er ging iets mis bij het importeren',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  // Step 4: Download cleaned version
  const downloadCleaned = () => {
    if (!importData) return;

    const cleanedData = importData.parsedData.map(row => ({
      'Jaar': row.year,
      'Grid Connection Savings': row.mapped.grid_connection_savings || 0,
      'Grid Purchase Costs Savings': row.mapped.grid_purchase_cost_savings || 0,
      'Dayahead Incomes': row.mapped.dayahead_incomes || 0,
      "HBE's": row.mapped.hbe_incomes || 0,
      'Energy Taxes Savings': row.mapped.energy_tax_savings || 0,
      'Endex Costs Savings': row.mapped.endex_cost_savings || 0,
      'Imbalance Income': row.mapped.imbalance_income || 0,
      'Imbalance Discount': row.mapped.imbalance_discount || 0,
      'EMS Costs': row.mapped.ems_costs || 0,
      'Total OPEX': row.mapped.total_opex || 0,
      'Generator Costs Savings': row.mapped.generator_cost_savings || 0,
      'Extra SDE': row.mapped.extra_sde || 0,
      'Total Solar Revenues': row.mapped.total_solar_revenues || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(cleanedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cleaned Data');
    
    XLSX.writeFile(wb, 'Businesscase_Cleaned.xlsx');

    toast({
      title: '⬇️ Download gestart',
      description: 'Businesscase_Cleaned.xlsx wordt gedownload',
    });
  };

  return {
    importData,
    isProcessing,
    handleFileUpload,
    updatePreviewRow,
    executeImport,
    downloadCleaned,
    hasData: !!importData,
  };
}
